/**
 * Role-based access control for the SoilHaul web UI.
 *
 * Backend enforces API-level permissions independently; this file only
 * controls which menu items and pages a signed-in user can see in the UI.
 *
 * Roles: admin, project_manager, finance, compliance_officer, dispatcher,
 *        driver, hauler, receiver, user.
 * `admin` always passes every check (kept out of the arrays for brevity).
 */

// Which non-admin roles can see each route. Admin sees everything.
export const ROUTE_ROLES = {
  "/":                          ["project_manager", "finance", "compliance_officer"],
  "/projects":                  ["project_manager", "finance", "compliance_officer"],
  "/loads":                     ["project_manager", "finance", "compliance_officer", "dispatcher", "driver", "hauler", "receiver"],
  "/tracking":                  ["project_manager", "compliance_officer", "dispatcher", "driver", "hauler", "receiver"],
  "/haulers":                   ["project_manager", "finance", "compliance_officer", "dispatcher"],
  "/sites":                     ["project_manager", "compliance_officer", "dispatcher", "receiver"],
  "/stockpiles":                ["project_manager", "compliance_officer", "dispatcher", "receiver"],
  "/hauler-invoices":           ["project_manager", "finance", "compliance_officer", "hauler"],
  "/hauling-summary":           ["project_manager", "finance", "compliance_officer"],
  "/verification":              ["project_manager", "compliance_officer"],
  "/exceptions":                ["project_manager", "compliance_officer", "dispatcher"],
  "/compliance":                ["project_manager", "compliance_officer"],
  "/fleet":                     ["compliance_officer", "dispatcher", "hauler"],
  "/drivers":                   ["compliance_officer", "dispatcher", "hauler"],
  "/audit":                     ["compliance_officer"],
  // Admin-exclusive: no other role listed.
  "/users":                     [],
  "/export":                    [],
  "/system/container-types":    [],
  "/system/material-types":     [],
  "/system/health":             [],
};

export function canAccess(role, path) {
  if (!role) return false;
  if (role === "admin") return true;
  const allow = ROUTE_ROLES[path];
  if (!allow) return false;
  return allow.includes(role);
}

/**
 * First path in `ordered` the given role can access. Used as the default
 * landing target when a user's role cannot see the Dashboard.
 */
export function defaultLanding(role) {
  const preferred = ["/", "/loads", "/hauler-invoices", "/tracking"];
  for (const p of preferred) {
    if (canAccess(role, p)) return p;
  }
  return "/loads"; // final fallback
}
